import type {
  CreatePrototypePayload,
  Paginated,
  PaginatedQuery,
  Prototype,
  UpdatePrototypePayload,
} from '@atlas/api-contract';

import { apiClient } from './client';

/**
 * `/v1/projects/:projectId/prototypes` and `/v1/prototypes/:id`.
 *
 * Thin on purpose, exactly like `projects.ts`: the axios interceptor has already
 * normalised every failure into an `ApiError`, and React Query owns caching, so
 * these stay callable from a loader, a mutation or a test.
 *
 * Note the asymmetry in the paths — a prototype is *listed* under its project
 * but *addressed* by its own id, because the id is already unique and pairing it
 * with a project in the URL would let a caller name the wrong parent.
 */

/** Request options every read accepts, so React Query can cancel it. */
interface ReadOptions {
  signal?: AbortSignal | undefined;
}

/**
 * `signal` is spread conditionally rather than passed as `undefined`: axios
 * declares it without `| undefined`, and `exactOptionalPropertyTypes` is on.
 */
const withSignal = ({ signal }: ReadOptions) =>
  signal === undefined ? {} : { signal };

/**
 * One page of a project's prototypes, filtered and sorted server-side
 * (spec R6.6, R6.8).
 *
 * An unknown `projectId` returns an empty page rather than a 404 — the list
 * endpoint filters, it does not assert the parent exists. Detecting a missing
 * project is `getProject`'s job, and the project detail route asks it first.
 */
export const listPrototypes = async (
  projectId: string,
  query: PaginatedQuery,
  options: ReadOptions = {},
): Promise<Paginated<Prototype>> => {
  const { data } = await apiClient.get<Paginated<Prototype>>(
    `/projects/${encodeURIComponent(projectId)}/prototypes`,
    { params: query, ...withSignal(options) },
  );

  return data;
};

/** One prototype. 404 when the id is unknown. */
export const getPrototype = async (
  prototypeId: string,
  options: ReadOptions = {},
): Promise<Prototype> => {
  const { data } = await apiClient.get<Prototype>(
    `/prototypes/${encodeURIComponent(prototypeId)}`,
    withSignal(options),
  );

  return data;
};

/** Creates a prototype. A name already used in this project comes back as 409. */
export const createPrototype = async (
  projectId: string,
  payload: CreatePrototypePayload,
): Promise<Prototype> => {
  const { data } = await apiClient.post<Prototype>(
    `/projects/${encodeURIComponent(projectId)}/prototypes`,
    payload,
  );

  return data;
};

/** Partial update. Only the keys present are changed. */
export const updatePrototype = async (
  prototypeId: string,
  payload: UpdatePrototypePayload,
): Promise<Prototype> => {
  const { data } = await apiClient.patch<Prototype>(
    `/prototypes/${encodeURIComponent(prototypeId)}`,
    payload,
  );

  return data;
};

/**
 * Deletes a prototype and, by cascade, every uploaded version, every file in
 * object storage and every comment thread left on them. Returns 204 with no
 * body.
 */
export const deletePrototype = async (prototypeId: string): Promise<void> => {
  await apiClient.delete(`/prototypes/${encodeURIComponent(prototypeId)}`);
};
